// Week view — design direction D «Warm Precision»: one hairline grid, day
// columns separated by 1px rules (no card gaps, no shadows), compact day
// heads with mono two-digit dates, and a 2px amber bar marking today (spec
// screen «Календарь»). All-day events sit on top, timed cards below sorted
// by time, capped at WEEK_COLUMN_CARD_CAP; the «+N more» expander opens a
// popover with every event of the day (compact rows, click → entity).
// Usability: a «+» button in each day head and a click on the empty zone
// open the quick-create picker (Task / Meeting) preset to that day, and the
// day heads are sticky so dates never scroll away. Every color comes from
// antd theme tokens, so the dark theme is the designed dark palette.

import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from '@govard/i18n-core/react';
import { Popover } from 'antd';
import { ClipboardList, Users } from 'lucide-react';
import { useMemo, useState, type ReactElement } from 'react';

import {
  groupEventsByDay,
  weekDays,
  WEEK_COLUMN_CARD_CAP,
  type PlannerEvent,
  type PlannerViewHandlers,
} from '../lib/plannerEvents.js';
import { PlannerEventCard } from './PlannerEventCard.js';

export type QuickCreateKind = 'task' | 'meeting';

export interface QuickCreateRequest {
  kind: QuickCreateKind;
  dateKey: string;
}

interface WeekViewProps extends PlannerViewHandlers {
  events: PlannerEvent[];
  weekStart: Dayjs;
  /** Quick-create: opened by the day «+» button or a click on the empty zone. */
  onQuickCreate?: ((request: QuickCreateRequest) => void) | undefined;
}

/** Compact overflow row: time (mono) + title + entity, click → entity. */
function OverflowRow({
  event,
  onOpen,
}: {
  event: PlannerEvent;
  onOpen: (event: PlannerEvent) => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={() => onOpen(event)}
      className="crm-hover-fill flex w-full items-baseline gap-2 border-0 bg-transparent px-1.5 py-1 text-left transition-colors duration-[110ms]"
      style={{ borderRadius: 'var(--ant-border-radius-sm, 6px)' }}
    >
      <span
        className="w-10 shrink-0 font-mono text-[10px] tabular-nums"
        style={{ color: 'var(--ant-color-text-tertiary)' }}
      >
        {event.timeLabel ?? '—'}
      </span>
      <span
        className="min-w-0 truncate text-[11px] font-semibold"
        style={{ color: 'var(--ant-color-text)' }}
      >
        {event.title}
      </span>
      <span
        className="ml-auto hidden max-w-[40%] truncate text-[10.5px] sm:block"
        style={{ color: 'var(--ant-color-text-tertiary)' }}
      >
        {event.entityLabel ?? ''}
      </span>
    </button>
  );
}

export function PlannerWeekView({
  events,
  weekStart,
  onOpen,
  onToggleTask,
  onQuickCreate,
}: WeekViewProps): ReactElement {
  const { t, locale } = useTranslation();
  const [quickDay, setQuickDay] = useState<string | null>(null);

  const grouped = useMemo(() => groupEventsByDay(events), [events]);
  const todayKey = dayjs().format('YYYY-MM-DD');
  const days = weekDays(weekStart);

  const requestQuickCreate = (kind: QuickCreateKind, dateKey: string): void => {
    setQuickDay(null);
    onQuickCreate?.({ kind, dateKey });
  };

  const quickCreateContent = (dateKey: string): ReactElement => (
    <div className="flex flex-col" style={{ minWidth: 132 }}>
      <button
        type="button"
        onClick={() => requestQuickCreate('task', dateKey)}
        className="crm-hover-fill flex items-center gap-2 border-0 bg-transparent px-2.5 py-1.5 text-left text-[12px] font-medium transition-colors duration-[110ms]"
        style={{ color: 'var(--ant-color-text)', borderRadius: 'var(--ant-border-radius-sm, 6px)' }}
      >
        <ClipboardList
          size={13}
          style={{ color: 'var(--ant-color-primary-border-hover, #d97706)' }}
        />
        {t('crm.calendar.quick_create.task')}
      </button>
      <button
        type="button"
        onClick={() => requestQuickCreate('meeting', dateKey)}
        className="crm-hover-fill flex items-center gap-2 border-0 bg-transparent px-2.5 py-1.5 text-left text-[12px] font-medium transition-colors duration-[110ms]"
        style={{ color: 'var(--ant-color-text)', borderRadius: 'var(--ant-border-radius-sm, 6px)' }}
      >
        <Users size={13} style={{ color: 'var(--ant-color-primary-border-hover, #d97706)' }} />
        {t('crm.calendar.quick_create.meeting')}
      </button>
    </div>
  );

  return (
    // Hairline grid: the 1px gap shows the container background, so day
    // separators stay 1px on every breakpoint without border math.
    <div
      className="grid grid-cols-1 gap-px overflow-hidden rounded-[6px] border sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7"
      style={{
        borderColor: 'var(--ant-color-border-secondary)',
        backgroundColor: 'var(--ant-color-border-secondary)',
      }}
    >
      {days.map((day) => {
        const dateKey = day.format('YYYY-MM-DD');
        const group = grouped.get(dateKey) ?? { allDay: [], timed: [] };
        const isToday = dateKey === todayKey;
        const isWeekend = day.day() === 0 || day.day() === 6;
        const count = group.allDay.length + group.timed.length;
        const shownTimed = group.timed.slice(0, WEEK_COLUMN_CARD_CAP);
        const hiddenCount = group.timed.length - shownTimed.length;

        return (
          <section
            key={dateKey}
            className="relative flex min-h-[220px] flex-col md:min-h-[420px]"
            style={{ backgroundColor: 'var(--ant-color-bg-container)' }}
          >
            {isToday ? (
              // «Сегодня» — amber marker: 2px bar along the column edge.
              <span
                aria-hidden="true"
                className="absolute inset-y-0 left-0 z-10 w-[2px]"
                style={{ backgroundColor: 'var(--ant-color-primary-border-hover, #d97706)' }}
              />
            ) : null}
            <header
              // Sticky day head: dates never scroll away with long columns.
              className="sticky top-0 z-[5] flex items-baseline gap-1.5 border-b px-2.5 pb-1.5 pt-2"
              style={{
                borderColor: 'var(--ant-color-border-secondary)',
                backgroundColor: 'var(--ant-color-bg-container)',
              }}
            >
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.06em]"
                style={{ color: 'var(--ant-color-text-tertiary)' }}
              >
                {day.locale(locale).format('dd')}
              </span>
              <span
                className="font-mono text-[11px] tabular-nums"
                style={{
                  color: isToday
                    ? 'var(--ant-color-primary-text)'
                    : isWeekend
                      ? 'var(--ant-color-text-tertiary)'
                      : 'var(--ant-color-text-secondary)',
                  fontWeight: isToday ? 600 : 500,
                }}
              >
                {day.format('DD')}
              </span>
              {count > 0 ? (
                <span
                  className="ml-1 font-mono text-[10px] tabular-nums"
                  style={{ color: 'var(--ant-color-text-tertiary)' }}
                >
                  {count}
                </span>
              ) : null}
              {onQuickCreate ? (
                <Popover
                  open={quickDay === dateKey}
                  onOpenChange={(open) => setQuickDay(open ? dateKey : null)}
                  content={quickCreateContent(dateKey)}
                  trigger="click"
                  placement="bottomRight"
                  styles={{ content: { padding: 4 } }}
                >
                  <button
                    type="button"
                    aria-label={t('crm.calendar.quick_create.add', {
                      date: day.locale(locale).format('D MMMM'),
                    })}
                    title={t('crm.calendar.quick_create.add', {
                      date: day.locale(locale).format('D MMMM'),
                    })}
                    onClick={() => setQuickDay(dateKey)}
                    className="ml-auto flex h-[18px] w-[18px] shrink-0 items-center justify-center border-0 bg-transparent text-[14px] font-semibold leading-none transition-colors duration-[110ms] hover:bg-[var(--color-surface-hover,var(--g-fill))]"
                    style={{
                      color: 'var(--ant-color-text-tertiary)',
                      borderRadius: 'var(--ant-border-radius-sm, 6px)',
                    }}
                  >
                    +
                  </button>
                </Popover>
              ) : null}
            </header>

            <div
              className="flex flex-1 flex-col gap-1 px-2 pb-2.5 pt-2"
              // Empty-zone quick create: a click that lands on the zone itself
              // (chips stop propagation) opens the picker for this day.
              onClick={
                onQuickCreate
                  ? (e) => {
                      if (e.target === e.currentTarget) setQuickDay(dateKey);
                    }
                  : undefined
              }
            >
              {group.allDay.length > 0 ? (
                <span
                  className="px-0.5 pt-0.5 text-[9.5px] font-semibold uppercase tracking-[1px]"
                  style={{ color: 'var(--ant-color-text-tertiary)' }}
                >
                  {t('crm.calendar.zone.all_day')}
                </span>
              ) : null}
              {group.allDay.map((event) => (
                <PlannerEventCard
                  key={event.id}
                  event={event}
                  onOpen={onOpen}
                  onToggleTask={onToggleTask}
                />
              ))}
              {shownTimed.map((event) => (
                <PlannerEventCard
                  key={event.id}
                  event={event}
                  onOpen={onOpen}
                  onToggleTask={onToggleTask}
                />
              ))}
              {hiddenCount > 0 ? (
                <Popover
                  content={
                    <div className="flex flex-col" style={{ minWidth: 240 }}>
                      <span
                        className="px-1.5 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]"
                        style={{ color: 'var(--ant-color-text-tertiary)' }}
                      >
                        {t('crm.calendar.overflow.title')}
                      </span>
                      {group.timed.map((event) => (
                        <OverflowRow key={event.id} event={event} onOpen={onOpen} />
                      ))}
                    </div>
                  }
                  trigger="click"
                  placement="right"
                  styles={{ content: { padding: 4 } }}
                >
                  <button
                    type="button"
                    className="mt-0.5 rounded-[4px] px-1 py-1.5 text-left font-mono text-[11px] font-semibold tabular-nums transition-colors duration-[110ms] hover:bg-[var(--color-surface-hover,var(--g-fill))] hover:text-[color:var(--ant-color-primary-text)]"
                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                  >
                    {t('crm.calendar.more_events', { count: hiddenCount })}
                  </button>
                </Popover>
              ) : null}
              {count === 0 ? (
                <div className="flex flex-1 items-center justify-center py-5">
                  {/* Quiet dashed free-day marker: no counts — we have no
                      working-hours data, only a calm "this day is empty".
                      No opacity dimming: tertiary on surface passes AA bare. */}
                  <span
                    className="text-[10.5px]"
                    style={{
                      color: 'var(--ant-color-text-tertiary)',
                      border: '1px dashed var(--ant-color-border-secondary)',
                      borderRadius: 'var(--ant-border-radius-sm, 6px)',
                      padding: '2px 10px',
                    }}
                  >
                    {t('crm.calendar.free_day')}
                  </span>
                </div>
              ) : null}
            </div>
          </section>
        );
      })}
    </div>
  );
}
